import { useEffect, useState } from "react";
import { Sparkles, Settings, ArrowLeft } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AdvisorChatWindow } from "@/components/AdvisorChatWindow";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAdvisor } from "@/context/AdvisorContext";
import { toast } from "sonner";
import { ADVISOR } from "@/constants/testIds";

const CONFIG = {
  employee: {
    testIds: ADVISOR.employee,
    title: "Asesor Personal",
    emptyStateText: "Pregúntame sobre tus políticas, vacaciones o incidencias. Solo veo tu propia información.",
    disclaimer: "Este asesor no sustituye asesoría legal. Para dudas legales o disputas, contacta a RH.",
    sessionPath: "/advisor/employee/session",
    chatPath: "/api/advisor/employee/chat",
    newSessionPath: "/advisor/employee/new-session",
  },
  admin: {
    testIds: ADVISOR.admin,
    title: "Consultor de RH",
    emptyStateText: "Pregúntame sobre cumplimiento, riesgos o estrategia de RH para tu empresa.",
    disclaimer: "Las recomendaciones y cifras deben validarse con un abogado laboral o contador antes de actuar.",
    sessionPath: "/advisor/admin/session",
    chatPath: "/api/advisor/admin/chat",
    newSessionPath: "/advisor/admin/new-session",
  },
};

export function AdvisorFab({ role }) {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [employeeContext, setEmployeeContext] = useState(null);
  const cfg = CONFIG[role];
  const advisor = useAdvisor();

  useEffect(() => {
    if (role !== "admin" || !advisor?.pending) return;
    setEmployeeContext({ id: advisor.pending.employeeId, name: advisor.pending.employeeName });
    setShowSettings(false);
    setOpen(true);
    advisor.clearPending();
  }, [advisor?.pending, role]);

  const openSettings = async () => {
    setShowSettings(true);
    setLoadingPrompt(true);
    try {
      const { data } = await api.get("/advisor/admin/prompt");
      setCustomPrompt(data.custom_prompt || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoadingPrompt(false);
    }
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await api.put("/advisor/admin/prompt", { custom_prompt: customPrompt });
      toast.success("Contexto del asesor actualizado");
      setShowSettings(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSavingPrompt(false);
    }
  };

  return (
    <>
      <div className={role === "employee" ? "fixed inset-x-0 bottom-0 max-w-md mx-auto pointer-events-none" : ""}>
        <button
          data-testid={cfg.testIds.fabButton}
          onClick={() => setOpen(true)}
          className={`pointer-events-auto ${
            role === "employee" ? "absolute bottom-24 right-5" : "fixed bottom-8 right-8 z-40"
          } w-14 h-14 rounded-full bg-terracotta text-white shadow-lg shadow-terracotta/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform z-40`}
        >
          <Sparkles className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </div>

      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShowSettings(false); }}>
        <SheetContent side="right" closeTestId={cfg.testIds.closeButton} className="w-full sm:max-w-md p-0 flex flex-col gap-0">
          <SheetHeader className="px-5 pt-5 pb-3 space-y-0">
            <div className="flex items-center justify-between pr-6">
              <SheetTitle data-testid={cfg.testIds.sheetTitle} className="flex items-center gap-2 font-heading">
                <Sparkles className="w-4 h-4 text-terracotta" /> {showSettings ? "Contexto del asesor" : cfg.title}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {showSettings ? "Configura el contexto adicional para el consultor de RH" : cfg.emptyStateText}
              </SheetDescription>
              {role === "admin" && !showSettings && (
                <button data-testid={cfg.testIds.promptSettingsButton} onClick={openSettings} className="text-charcoal/40 hover:text-petroleum transition-colors" title="Personalizar contexto">
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 px-4 pb-4 overflow-hidden">
            {showSettings ? (
              <div className="flex flex-col h-full gap-3">
                <button
                  data-testid={cfg.testIds.promptBackButton}
                  onClick={() => setShowSettings(false)}
                  className="flex items-center gap-1.5 text-xs text-charcoal/50 hover:text-petroleum self-start"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Volver al chat
                </button>
                <p className="text-sm text-charcoal/70">
                  Agrega tono y contexto de tu empresa (opcional). Esto <strong>no reemplaza</strong> las reglas de seguridad del asesor
                  — los avisos legales y los límites de la conversación siempre se mantienen fijos.
                </p>
                <Textarea
                  data-testid={cfg.testIds.promptTextarea}
                  value={loadingPrompt ? "" : customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  disabled={loadingPrompt}
                  className="flex-1 min-h-32 bg-white resize-none disabled:opacity-60"
                  placeholder={loadingPrompt ? "Cargando..." : "Ej. Somos una ferretería familiar de 18 empleados; prioriza retención y evita recomendar despidos como primera opción."}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setShowSettings(false)} data-testid={cfg.testIds.promptCancelButton}>
                    Cancelar
                  </Button>
                  <Button onClick={savePrompt} disabled={savingPrompt || loadingPrompt} data-testid={cfg.testIds.promptSaveButton} className="bg-terracotta hover:bg-terracotta/90">
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              <AdvisorChatWindow
                sessionPath={cfg.sessionPath}
                chatPath={cfg.chatPath}
                newSessionPath={cfg.newSessionPath}
                testIds={cfg.testIds}
                emptyStateText={cfg.emptyStateText}
                disclaimer={cfg.disclaimer}
                contextEmployee={role === "admin" ? employeeContext : null}
                onClearContext={() => setEmployeeContext(null)}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
